import { Navigation } from "./Index/components/Navigation";
import { Footer } from "./Index/components/Footer";
import { useLanguage } from "@/contexts/LanguageContext";
import { translations } from "@/translations";
import { Calendar, Clock, Users, Star, BookOpen } from "lucide-react";
import { motion } from "framer-motion";
import { OpenHouseBanner } from "@/components/OpenHouseBanner";
import { OptimizedPicture } from "@/components/OptimizedPicture";
import { usePageMeta } from "@/hooks/usePageMeta";

const META: Record<string, { title: string; description: string }> = {
  es: {
    title: "Calendario escolar | Gándara International School",
    description: "Calendario escolar, horarios y jornada lectiva en Gándara International School, Gondomar. Consulta las fechas clave del curso.",
  },
  en: {
    title: "School Calendar | Gándara International School",
    description: "School calendar, schedules and school day at Gándara International School, Gondomar. Check key dates for the school year.",
  },
  pt: {
    title: "Calendário Escolar | Gándara International School",
    description: "Calendário escolar, horários e dia letivo na Gándara International School, Gondomar. Consulte as datas-chave do ano letivo.",
  },
  fr: {
    title: "Calendrier Scolaire | Gándara International School",
    description: "Calendrier scolaire, horaires et journée scolaire à Gándara International School, Gondomar. Consultez les dates clés de l'année.",
  },
};

const SchoolCalendar = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const meta = META[language] || META.es;
  usePageMeta({ title: meta.title, description: meta.description });

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <OpenHouseBanner />
      
      <div className="container mx-auto px-4 pt-32 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-6xl mx-auto"
        >
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex justify-center mb-4">
              <div className="bg-primary/10 p-4 rounded-full">
                <Calendar className="w-12 h-12 text-primary" />
              </div>
            </div>
            <h1 className="text-5xl font-light text-primary mb-6">
              {t.schoolCalendar.title}
            </h1>
            <p className="text-lg text-muted-foreground mb-12 max-w-2xl mx-auto">
              {t.schoolCalendar.subtitle}
            </p>
            
            {/* Calendar Image (meses; los datos van en las tarjetas de abajo) */}
            <div className="mb-4">
              <a
                href="/lovable-uploads/calendario-2026-2027-v2.png"
                target="_blank"
                rel="noopener noreferrer"
                title={t.schoolCalendar.title}
                className="block"
              >
                <OptimizedPicture 
                  src="/lovable-uploads/calendario-2026-2027-v2.png" 
                  alt={t.schoolCalendar.subtitle}
                  width={1600}
                  height={820}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-auto rounded-2xl shadow-xl"
                />
              </a>
            </div>
            <p className="text-sm text-muted-foreground mb-16">
              {({ es: "Toca el calendario para ampliarlo o imprimirlo", en: "Tap the calendar to enlarge or print it", pt: "Toque no calendário para ampliar ou imprimir", fr: "Touchez le calendrier pour l'agrandir ou l'imprimer" } as Record<string, string>)[language] || "Toca el calendario para ampliarlo o imprimirlo"}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Course Dates */}
            <motion.div 
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.2 }}
              className="bg-card/90 backdrop-blur-sm p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow border border-primary/10"
            >
              <div className="bg-primary/10 -mx-8 -mt-8 mb-6 p-6 rounded-t-2xl">
                <div className="flex items-center">
                  <BookOpen className="w-6 h-6 text-primary mr-3" />
                  <h2 className="text-2xl font-medium text-primary">{t.schoolCalendar.courseDate.title}</h2>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex justify-between items-center py-2 border-b border-border/50">
                  <span className="font-medium">{t.schoolCalendar.courseDate.start}</span>
                  <span className="text-primary font-semibold">{t.schoolCalendar.courseDate.startDate}</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="font-medium">{t.schoolCalendar.courseDate.end}</span>
                  <span className="text-primary font-semibold">{t.schoolCalendar.courseDate.endDate}</span>
                </div>
              </div>
            </motion.div>

            {/* Holidays */}
            <motion.div 
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.2 }}
              className="bg-card backdrop-blur-sm p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow border"
            >
              <div className="bg-accent/10 -mx-8 -mt-8 mb-6 p-6 rounded-t-2xl">
                <div className="flex items-center">
                  <Star className="w-6 h-6 text-primary mr-3" />
                  <h2 className="text-2xl font-medium text-primary">{t.schoolCalendar.holidays.title}</h2>
                </div>
              </div>
              <div className="space-y-3">
                {t.schoolCalendar.holidays.items.map((holiday, index) => (
                    <div key={index} className="flex justify-between items-start py-2 border-b last:border-b-0">
                    <span className="font-medium">{holiday.name}</span>
                    <span className="text-primary font-semibold text-right text-sm">{holiday.date}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Public Holidays */}
            <motion.div 
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.2 }}
              className="bg-card/90 backdrop-blur-sm p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow border border-primary/10"
            >
              <div className="bg-primary/10 -mx-8 -mt-8 mb-6 p-6 rounded-t-2xl">
                <div className="flex items-center">
                  <Calendar className="w-6 h-6 text-primary mr-3" />
                  <h2 className="text-2xl font-medium text-primary">{t.schoolCalendar.publicHolidays.title}</h2>
                </div>
              </div>
              <div className="space-y-3">
                {t.schoolCalendar.publicHolidays.items.map((holiday, index) => (
                  <div key={index} className="flex justify-between items-start py-2 border-b border-border/50 last:border-b-0">
                    <span className="font-medium">{holiday.name}</span>
                    <span className="text-primary font-semibold text-right">{holiday.date}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Non-school Days */}
            <motion.div 
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.2 }}
              className="bg-card/90 backdrop-blur-sm p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow border border-accent/10"
            >
              <div className="bg-accent/10 -mx-8 -mt-8 mb-6 p-6 rounded-t-2xl">
                <div className="flex items-center">
                  <Clock className="w-6 h-6 text-primary mr-3" />
                  <h2 className="text-2xl font-medium text-primary">{t.schoolCalendar.nonSchoolDays.title}</h2>
                </div>
              </div>
              <div className="space-y-3">
                {t.schoolCalendar.nonSchoolDays.items.map((day, index) => (
                  <div key={index} className="flex justify-between items-start py-2 border-b border-border/50 last:border-b-0">
                    <span className="font-medium">{day.name}</span>
                    <span className="text-primary font-semibold text-right">{day.date}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Family Celebrations */}
            <motion.div 
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.2 }}
              className="bg-card/90 backdrop-blur-sm p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow border border-primary/10"
            >
              <div className="bg-primary/10 -mx-8 -mt-8 mb-6 p-6 rounded-t-2xl">
                <div className="flex items-center">
                  <Users className="w-6 h-6 text-primary mr-3" />
                  <h2 className="text-2xl font-medium text-primary">{t.schoolCalendar.familyCelebrations.title}</h2>
                </div>
              </div>
              <div className="space-y-3">
                {t.schoolCalendar.familyCelebrations.items.map((celebration, index) => (
                  <div key={index} className="flex justify-between items-start py-2 border-b border-border/50 last:border-b-0">
                    <span className="font-medium">{celebration.name}</span>
                    <span className="text-primary font-semibold text-right">{celebration.date}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Family Meetings */}
            <motion.div 
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.2 }}
              className="bg-card/90 backdrop-blur-sm p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow border border-accent/10"
            >
              <div className="bg-accent/10 -mx-8 -mt-8 mb-6 p-6 rounded-t-2xl">
                <div className="flex items-center">
                  <Users className="w-6 h-6 text-primary mr-3" />
                  <h2 className="text-2xl font-medium text-primary">{t.schoolCalendar.familyMeetings.title}</h2>
                </div>
              </div>
              <div className="space-y-3">
                {t.schoolCalendar.familyMeetings.items.map((meeting, index) => (
                  <div key={index} className="flex justify-between items-start py-2 border-b border-border/50 last:border-b-0">
                    <span className="font-medium">{meeting.name}</span>
                    <span className="text-primary font-semibold text-right">{meeting.date}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>

      <Footer />
    </div>
  );
};

export default SchoolCalendar;